import {COMPANY, GROUP, TEAM} from '../enums/adminLevels';
import {getPermissions} from '../state/auth/authSelector';
import {useSelector} from '../state/hooks';
import {getSelectedGroupId} from '../state/team/teamSelectors';

type Team = {
  id: number;
  roles?: Permission;
};

type Group = {
  id: number;
  roles?: Permission;
};

type Permission = {
  isAnalyst: boolean;
  isEditor: boolean;
  isManager: boolean;
  isLead: boolean;
};

type Permissions = {
  [COMPANY]: Permission | false;
  [GROUP]: Permission | Group[] | false;
  [TEAM]: Permission | Team[] | false;
};

export const getAdminLevel = (permissions: Permissions) => {
  if (permissions.company) {
    return COMPANY;
  } else if (permissions.group) {
    return GROUP;
  } else {
    return TEAM;
  }
};

export const getHasLeadRightsOnCompany = (permissions: Permissions) =>
  permissions.company ? permissions.company.isLead : false;

export const getHasLeadRightsOnGroup = (
  permissions: Permissions,
  group: Group
) => {
  const isGroupLead =
    group && Array.isArray(permissions.group)
      ? (permissions.group.find((g) => g.id === group.id)?.roles?.isLead ??
        false)
      : false;
  return getHasLeadRightsOnCompany(permissions) || isGroupLead;
};

export const getHasLeadRightsOnTeam = (
  permissions: Permissions,
  group: Group,
  team: Team
) =>
  getHasLeadRightsOnCompany(permissions) ||
  getHasLeadRightsOnGroup(permissions, group) ||
  (Array.isArray(permissions.team) &&
    (permissions.team.find((t) => t.id === team.id)?.roles?.isLead ?? false));

export function useContentPermissions() {
  const permissions = useSelector(getPermissions);
  const selectedGroup = useSelector(getSelectedGroupId);
  const isLeadForCurrentGroup = getHasLeadRightsOnGroup(permissions, {
    id: selectedGroup,
  });
  const isCompanyLead = getHasLeadRightsOnCompany(permissions);

  return {
    canManageContent: isLeadForCurrentGroup || isCompanyLead,
    permissions,
  };
}
